import React, { useMemo, useState } from 'react'
import { useSequencerStore } from '@/stores/sequencerStore'
import { DEFAULT_BANK_IDS, type BankId } from '@busswerks/shared'

/**
 * Four-bank switcher (A/B/C/D) that sits above the pattern bar.
 * Clicking a bank makes it active — the pattern bar updates to show
 * that bank's patterns, the pad grid swaps to the bank's kit/pad
 * layout, and the editor's current pattern becomes whatever the
 * bank last had active (auto-seeded with a fresh pattern if empty).
 *
 * Double-click a bank pill to rename it.
 */
export const BankSwitcher: React.FC = () => {
  const containers = useSequencerStore(s => s.containers)
  const currentContainerId = useSequencerStore(s => s.currentContainerId)
  const selectBank = useSequencerStore(s => s.selectBank)
  const renameBank = useSequencerStore(s => s.renameBank)
  const isPlaying = useSequencerStore(s => s.isPlaying)
  const queuedBankId = useSequencerStore(s => s.queuedBankId)
  const queueBankSwitch = useSequencerStore(s => s.queueBankSwitch)
  const cancelQueuedSwitches = useSequencerStore(s => s.cancelQueuedSwitches)

  const container = containers[currentContainerId]
  const banks = useMemo(
    () => DEFAULT_BANK_IDS.map(id => container?.banks[id]).filter(Boolean),
    [container],
  )

  const [renamingId, setRenamingId] = useState<BankId | null>(null)
  const [renameValue, setRenameValue] = useState('')

  const commitRename = () => {
    if (renamingId && renameValue.trim()) {
      renameBank(renamingId, renameValue.trim())
    }
    setRenamingId(null)
  }

  if (!container) return null

  return (
    <nav
      aria-label="Pad banks"
      className="inline-flex items-center gap-1 rounded-lg bg-surface-sunken border border-brd-subtle p-1"
    >
      {banks.map(bank => {
        if (!bank) return null
        const active = bank.id === container.activeBankId
        const queued = queuedBankId === bank.id && !active
        const isRenaming = renamingId === bank.id
        const patternCount = bank.patternIds.length
        return isRenaming ? (
          <input
            key={bank.id}
            autoFocus
            type="text"
            value={renameValue}
            onChange={e => setRenameValue(e.target.value)}
            onBlur={commitRename}
            onKeyDown={e => {
              if (e.key === 'Enter') commitRename()
              if (e.key === 'Escape') setRenamingId(null)
            }}
            className="w-24 px-2 py-0.5 rounded bg-surface-raised border border-[color:var(--accent-halo)] text-xs font-mono text-content-primary focus:outline-none"
          />
        ) : (
          <button
            key={bank.id}
            type="button"
            aria-pressed={active}
            onClick={() => {
              if (active) {
                // Re-clicking the active bank cancels a pending queue.
                if (queuedBankId) cancelQueuedSwitches()
                return
              }
              if (isPlaying) {
                // Toggle: clicking the already-queued bank cancels it.
                if (queuedBankId === bank.id) cancelQueuedSwitches()
                else queueBankSwitch(bank.id)
              } else {
                selectBank(bank.id)
              }
            }}
            onDoubleClick={() => {
              setRenamingId(bank.id)
              setRenameValue(bank.label)
            }}
            title={
              queued
                ? `${bank.label} — queued for next downbeat (click again to cancel)`
                : `${bank.label} — ${patternCount} pattern${patternCount === 1 ? '' : 's'} · double-click to rename`
            }
            className={`relative px-2.5 py-1 rounded-md text-xs font-mono transition-colors ${
              active
                ? 'bg-accent-muted text-accent border border-[color:var(--accent-halo)]'
                : queued
                  ? 'bg-surface-raised text-content-primary border border-[color:var(--accent-halo)] animate-pulse'
                  : 'text-content-secondary hover:text-content-primary hover:bg-surface-raised/60'
            }`}
          >
            <span className="font-semibold">{bank.id}</span>
            {patternCount > 0 && (
              <span
                aria-hidden
                className={`ml-1 text-[9px] ${active ? 'text-accent/70' : queued ? 'text-accent' : 'text-content-tertiary'}`}
              >
                {patternCount}
              </span>
            )}
            {queued && (
              <span
                aria-hidden
                className="absolute -top-1 -right-1 w-2 h-2 rounded-full bg-accent animate-pulse"
                title="Queued"
              />
            )}
          </button>
        )
      })}
    </nav>
  )
}
